import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Spinner } from '../Spinner';
import { FaIcon } from '../FaIcon';
import { previewFrameGeometry } from './portalPreviewFrame';
import {
    BRAND_SOURCE_AGENCY, BRAND_SOURCE_CUSTOM, BRAND_SOURCE_ICON, BRAND_SOURCES,
    agencyHasBrand, brandingForSource, isPreviewReadyMessage, postPreviewBranding,
} from './portalBrandSource';

const PORTAL_THEMES = ['EDITORIAL', 'MINIMAL', 'IMMERSIVE'];

// Lo que el campo acepta. Tiene que decir lo MISMO que el backend (`branding_logo_storage`), que
// es el control de verdad: esto es conveniencia — evita subir 800 MB por la red para que el
// servidor los rechace al final. Exportados porque el avatar de la ficha 2.1 (el ícono del cliente) y
// el logo de la agencia en su Perfil suben por el mismo storage, con las mismas reglas.
export const LOGO_ACCEPTED_TYPES = 'image/png,image/jpeg,image/gif,image/webp';
export const LOGO_MAX_BYTES = 5 * 1024 * 1024;

// Mini-preview de cada template. El acento usa el color de marca del cliente
// (o el naranja Dashbo por defecto), igual que el portal real.
function renderThemeThumb(themeId, accentColor) {
    const accent = accentColor || '#FF470F';
    const tint = accent + '22';
    const tile = { width: '15px', height: '15px', borderRadius: '4px', background: '#E9E5DE', flexShrink: 0 };
    const line = { height: '5px', width: '60%', borderRadius: '3px', background: '#E3DED6' };
    const miniCard = (btn, extra = {}) => (
        <div style={{
            flex: 1, background: '#fff', borderRadius: '7px', padding: '8px',
            display: 'flex', flexDirection: 'column', gap: '6px', ...extra,
        }}>
            <div style={tile} />
            <div style={line} />
            {btn}
        </div>
    );
    if (themeId === 'EDITORIAL') {
        return (
            <div style={{ height: '100%', background: '#FBFAF6', display: 'flex', flexDirection: 'column' }}>
                <div style={{ background: '#fff', borderBottom: '1px solid #ECE7DD', padding: '8px 10px', display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '13px', fontWeight: 700, color: '#2A2A28' }}>Aa</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                        <span style={{ fontSize: '7px', letterSpacing: '1px', color: '#7A746A' }}>•&nbsp;•&nbsp;•</span>
                        <span style={{ height: '2px', width: '18px', background: accent, borderRadius: '2px' }} />
                    </span>
                </div>
                <div style={{ display: 'flex', gap: '8px', padding: '10px' }}>
                    {miniCard(<div style={{ height: '13px', borderRadius: '4px', background: accent, marginTop: '2px' }} />)}
                    {miniCard(<div style={{ height: '13px', borderRadius: '4px', background: accent, marginTop: '2px' }} />)}
                </div>
            </div>
        );
    }
    if (themeId === 'MINIMAL') {
        return (
            <div style={{ height: '100%', background: '#F5F6F8', display: 'flex', flexDirection: 'column' }}>
                <div style={{ background: '#fff', borderBottom: '1px solid #E6E8EB', padding: '8px 10px', display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'system-ui, sans-serif', fontSize: '11px', fontWeight: 800, color: '#1F2430' }}>Aa</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ display: 'flex', gap: '2px', background: '#EEF0F2', borderRadius: '6px', padding: '2px' }}>
                        <span style={{ width: '16px', height: '8px', borderRadius: '4px', background: '#fff', boxShadow: '0 1px 1px rgba(0,0,0,.08)' }} />
                        <span style={{ width: '12px', height: '8px', borderRadius: '4px', background: 'transparent' }} />
                        <span style={{ width: '12px', height: '8px', borderRadius: '4px', background: 'transparent' }} />
                    </span>
                </div>
                <div style={{ display: 'flex', gap: '8px', padding: '10px' }}>
                    {miniCard(<div style={{ height: '13px', borderRadius: '4px', background: tint, marginTop: '2px' }} />, { border: '1px solid #E6E8EB' })}
                    {miniCard(<div style={{ height: '13px', borderRadius: '4px', background: tint, marginTop: '2px' }} />, { border: '1px solid #E6E8EB' })}
                </div>
            </div>
        );
    }
    return (
        <div style={{ height: '100%', background: '#FCFCFD', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: accent, padding: '9px 10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontFamily: 'system-ui, sans-serif', fontSize: '11px', fontWeight: 700, color: '#fff' }}>Aa</span>
                <span style={{ flex: 1 }} />
                <span style={{ width: '20px', height: '9px', borderRadius: '5px', background: '#fff', opacity: .95 }} />
                <span style={{ width: '12px', height: '9px', borderRadius: '5px', background: 'rgba(255,255,255,.4)' }} />
            </div>
            <div style={{ display: 'flex', gap: '8px', padding: '10px' }}>
                {miniCard(<div style={{ height: '13px', borderRadius: '7px', background: accent, marginTop: '2px' }} />, { boxShadow: '0 2px 6px rgba(20,24,31,.10)' })}
                {miniCard(<div style={{ height: '13px', borderRadius: '7px', background: accent, marginTop: '2px' }} />, { boxShadow: '0 2px 6px rgba(20,24,31,.10)' })}
            </div>
        </div>
    );
}

/**
 * Zona "Apariencia" del portal del cliente: marca + diseño (template visual), con un preview EN VIVO
 * del portal real a la derecha (iframe a /client/:id/preview).
 *
 * La marca se elige entre tres opciones (ronda 2 de la tanda iVirtual, D11-D12, 09/10): la de la
 * agencia (default y recomendada), el ícono del cliente o una marca propia. El formulario de marca
 * propia es el de siempre, con la subida que persiste en el acto; elegir otra opción es un borrador
 * hasta «Guardar marca». La vista previa muestra el borrador ANTES de guardar: se lo manda al iframe
 * por `postMessage` (`portalBrandSource.js`). El iframe se remonta vía `previewToken` cuando se guarda
 * la marca o se cambia el tema, y al montarse pide el borrador de nuevo.
 */
export function PortalAppearanceZone({
    clientId, branding, onBrandingChange, onSaveBranding, savingBranding,
    portalTheme, onSelectTheme, savingTheme, previewToken,
    onUploadLogo, onRemoveLogo, onRejectLogo, uploadingLogo,
    brandSource = BRAND_SOURCE_CUSTOM, onBrandSourceChange, iconUrl = null, agencyBranding = null,
}) {
    const { t } = useTranslation();
    const draftBranding = brandingForSource(brandSource, branding, iconUrl);
    const accent = draftBranding.brand_primary_color
        || (agencyBranding && agencyBranding.brand_primary_color) || '#FF470F';
    const fileInputRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    // La URL a mano deja de ser el camino principal, pero no se va: hay agencias que ya tienen
    // cargado el logo de su propio CDN y ese caso sigue siendo legítimo. Arranca cerrada, y abierta
    // si el cliente ya tiene una URL puesta (para que se vea qué está configurado hoy).
    const [showUrlField, setShowUrlField] = useState(!!branding.logo_url);

    // La vista previa recibe el borrador: cuando cambia, y cuando el iframe (recién montado) avisa que
    // ya escucha. El ref evita re-enganchar el listener en cada tecla.
    const frameRef = useRef(null);
    const draftRef = useRef(draftBranding);
    draftRef.current = draftBranding;
    const draftKey = JSON.stringify(draftBranding);
    useEffect(() => {
        const onMessage = (event) => {
            if (isPreviewReadyMessage(event, frameRef.current)) {
                postPreviewBranding(frameRef.current, draftRef.current);
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);
    useEffect(() => {
        postPreviewBranding(frameRef.current, draftRef.current);
    }, [draftKey]);

    // El `accept` del input sólo filtra el diálogo del sistema: arrastrar y soltar lo ignora por
    // completo. Sin este chequeo, soltar un video lo subía entero para que el backend lo rechazara
    // al llegar. El control real sigue siendo el del servidor; esto le ahorra el viaje al usuario.
    const pickFile = (file) => {
        if (!file || uploadingLogo) return;
        if (!LOGO_ACCEPTED_TYPES.split(',').includes(file.type)) {
            onRejectLogo && onRejectLogo('portal_tab.branding_logo_error_UNSUPPORTED_TYPE');
            return;
        }
        if (file.size > LOGO_MAX_BYTES) {
            onRejectLogo && onRejectLogo('portal_tab.branding_logo_error_TOO_LARGE');
            return;
        }
        onUploadLogo(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        pickFile(e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]);
    };

    // La caja de la vista previa: lo que mide define la escala del portal (`previewFrameGeometry`).
    // Se re-mide al cambiar de ancho (sidebar que se colapsa, ventana que se achica).
    const frameBoxRef = useRef(null);
    const [frameBox, setFrameBox] = useState(null);
    useEffect(() => {
        const box = frameBoxRef.current;
        if (!box) return undefined;
        const measure = () => {
            const width = box.clientWidth;
            const height = box.clientHeight;
            setFrameBox(prev => (prev && prev.width === width && prev.height === height) ? prev : {width, height});
        };
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(box);
        return () => observer.disconnect();
    }, []);
    const frameGeometry = frameBox ? previewFrameGeometry(frameBox.width, frameBox.height) : null;
    // Hasta medir, el iframe ocupa la caja sin escalar (CSS): nunca desborda.
    const frameStyle = frameGeometry ? {
        width: `${frameGeometry.width}px`,
        height: `${frameGeometry.height}px`,
        transform: `scale(${frameGeometry.scale})`,
    } : undefined;

    const sourceLabels = {
        [BRAND_SOURCE_AGENCY]: 'portal_tab.brand_source_agency',
        [BRAND_SOURCE_ICON]: 'portal_tab.brand_source_icon',
        [BRAND_SOURCE_CUSTOM]: 'portal_tab.brand_source_custom',
    };

    // Las tres opciones son UNA decisión, así que van en un solo control (filas de un mismo grupo, no
    // tres tarjetas). Cada fila muestra a la derecha el logo que esa opción pondría en el portal: la
    // pregunta del usuario es «¿qué logo va a ver mi cliente?», y la miniatura la contesta sin leer.
    // `inherited`: la marca propia sin logo propio hereda, campo por campo, el de la agencia (como
    // `resolved_branding()`), así que la miniatura muestra ESE logo, punteada (review C-iv-icono-y-marca-04).
    const agencyLogo = (agencyBranding && agencyBranding.logo_url) || null;
    const thumbFor = (source) => {
        if (source === BRAND_SOURCE_AGENCY) return {url: agencyLogo, inherited: false};
        if (source === BRAND_SOURCE_ICON) return {url: iconUrl || null, inherited: false};
        if (branding.logo_url) return {url: branding.logo_url, inherited: false};
        return {url: agencyLogo, inherited: !!agencyLogo};
    };

    const renderSourceOption = (source) => {
        const disabled = source === BRAND_SOURCE_ICON && !iconUrl;
        const selected = brandSource === source;
        const thumb = thumbFor(source);
        return (
            <label key={source} data-testid={`pm-brand-source-${source}`}
                   className={`pm-brand-source${selected ? ' -active' : ''}${disabled ? ' -disabled' : ''}`}>
                <span className="pm-brand-source-text">
                    <span className="pm-brand-source-head">
                        <input type="radio" name={`pm-brand-source-${clientId}`} value={source}
                               checked={selected} disabled={disabled}
                               onChange={() => onBrandSourceChange && onBrandSourceChange(source)}/>
                        <span className="pm-brand-source-title">{t(sourceLabels[source])}</span>
                    </span>
                    {disabled && (
                        <span className="pm-brand-source-note">{t('portal_tab.brand_source_icon_missing')}</span>
                    )}
                    {source === BRAND_SOURCE_AGENCY && selected && renderAgencyBrand()}
                </span>
                <span className={`pm-brand-source-thumb${thumb.url ? '' : ' -empty'}${thumb.inherited ? ' -inherited' : ''}`}
                      aria-hidden="true" data-testid={`pm-brand-source-thumb-${source}`}>
                    {thumb.url && <img src={thumb.url} alt=""/>}
                </span>
            </label>
        );
    };

    // Lo que hereda el cliente con la primera opción, dentro de su fila: el nombre y los dos colores (el
    // logo ya está en la miniatura). `agencyBranding` en null = no se pudo leer: no se afirma nada.
    const renderAgencyBrand = () => {
        if (!agencyBranding) return null;
        if (!agencyHasBrand(agencyBranding)) {
            return (
                <span className="pm-brand-source-note -missing" data-testid="pm-brand-agency-missing">
                    <Trans i18nKey="portal_tab.agency_brand_missing"
                           components={[<Link key="profile" to="/agency/profile"/>]}/>
                </span>
            );
        }
        const name = agencyBranding.display_name_for_clients || agencyBranding.name;
        return (
            <span className="pm-brand-source-detail" data-testid="pm-brand-agency">
                <span className="pm-brand-source-detail-name">{name}</span>
                {[agencyBranding.brand_primary_color, agencyBranding.brand_secondary_color]
                    .filter(Boolean).map((color, index) => (
                        <span key={`${index}-${color}`} className="pm-brand-source-swatch"
                              style={{background: color}} title={color}/>
                    ))}
            </span>
        );
    };

    // Logo | nombre en una fila y los dos colores en la otra: el reparto de siempre del formulario.
    const renderBrandFields = (logoItem) => (
        <>
            <div className="form-grid row-col-2">
                {logoItem}
                <div className="form-item">
                    <label>{t('portal_tab.branding_display_name')}</label>
                    <input type="text" name="display_name_for_clients"
                        value={branding.display_name_for_clients} onChange={onBrandingChange} />
                </div>
            </div>
            <div className="form-grid row-col-2">
                <div className="form-item">
                    <label>{t('portal_tab.branding_primary_color')}</label>
                    <div className="brand-color-input">
                        <input type="color"
                            value={branding.brand_primary_color || '#FF470F'}
                            onChange={e => onBrandingChange({ target: { name: 'brand_primary_color', value: e.target.value } })} />
                        <input type="text" name="brand_primary_color"
                            value={branding.brand_primary_color}
                            onChange={onBrandingChange} placeholder="#FF470F" maxLength={7} />
                    </div>
                </div>
                <div className="form-item">
                    <label>{t('portal_tab.branding_secondary_color')}</label>
                    <div className="brand-color-input">
                        <input type="color"
                            value={branding.brand_secondary_color || '#440631'}
                            onChange={e => onBrandingChange({ target: { name: 'brand_secondary_color', value: e.target.value } })} />
                        <input type="text" name="brand_secondary_color"
                            value={branding.brand_secondary_color}
                            onChange={onBrandingChange} placeholder="#440631" maxLength={7} />
                    </div>
                </div>
            </div>
            <p className="field-footnote">{t('portal_tab.branding_override_hint')}</p>
        </>
    );

    const renderCustomLogo = () => (
        <div className="form-item">
            <label>{t('portal_tab.branding_logo')}</label>
            <div
                className={`logo-dropzone ${dragging ? '-dragging' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
            >
                {branding.logo_url ? (
                    <img className="logo-dropzone-preview" src={branding.logo_url}
                        alt={t('portal_tab.branding_logo')} />
                ) : (
                    <div className="logo-dropzone-empty"><FaIcon iconName="image" /></div>
                )}
                <div className="logo-dropzone-actions">
                    {uploadingLogo ? (
                        <Spinner color="positive small" />
                    ) : (
                        <>
                            <button type="button" className="btn -alt -small"
                                onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                                {branding.logo_url
                                    ? t('portal_tab.branding_logo_replace')
                                    : t('portal_tab.branding_logo_upload')}
                            </button>
                            {branding.logo_url && (
                                <button type="button" className="logo-dropzone-remove"
                                    onClick={onRemoveLogo}>
                                    {t('portal_tab.branding_logo_remove')}
                                </button>
                            )}
                        </>
                    )}
                </div>
                <input ref={fileInputRef} type="file"
                    accept={LOGO_ACCEPTED_TYPES}
                    style={{ display: 'none' }}
                    onChange={(e) => { pickFile(e.target.files && e.target.files[0]); e.target.value = ''; }} />
            </div>
            <p className="field-footnote logo-dropzone-hint">{t('portal_tab.branding_logo_hint')}</p>
            <button type="button" className="logo-url-toggle"
                onClick={() => setShowUrlField(v => !v)}>
                {t('portal_tab.branding_logo_use_url')}
            </button>
            {showUrlField && (
                <>
                    <label htmlFor="portal-logo-url">
                        {t('portal_tab.branding_logo_url')}
                    </label>
                    <input id="portal-logo-url" type="text" name="logo_url"
                        placeholder="https://..."
                        value={branding.logo_url} onChange={onBrandingChange} />
                </>
            )}
        </div>
    );

    const renderIconLogo = () => (
        <div className="form-item">
            <label>{t('portal_tab.branding_logo')}</label>
            <div className="logo-dropzone -readonly" data-testid="pm-brand-icon-logo">
                <img className="logo-dropzone-preview" src={iconUrl} alt={t('portal_tab.branding_logo')}/>
            </div>
        </div>
    );

    return (
        <div className="box portal-tab-config pm-appearance">
            <div className="box-header">
                <h3 className="box-title">{t('portal_modules.appearance_title')}</h3>
            </div>
            <div className="portal-tab-config-body pm-appearance-body">
                <div className="pm-appearance-form">
                    {/* Marca: de dónde sale (agencia / ícono / propia) */}
                    <div className="pm-brand-sources" role="radiogroup"
                         aria-label={t('portal_tab.branding_section_title')}>
                        {BRAND_SOURCES.map(renderSourceOption)}
                    </div>
                    {brandSource === BRAND_SOURCE_ICON && renderBrandFields(renderIconLogo())}
                    {brandSource === BRAND_SOURCE_CUSTOM && renderBrandFields(renderCustomLogo())}
                    <div className="submit-row">
                        {savingBranding ? (
                            <Spinner color="positive small" />
                        ) : (
                            <button type="button" className="btn -alt" onClick={onSaveBranding}
                                    data-testid="pm-brand-save">
                                {t('portal_tab.branding_save')}
                            </button>
                        )}
                    </div>

                    <div className="pm-appearance-divider" />

                    {/* Diseño / template visual */}
                    <p className="field-footnote" style={{ marginBottom: '1em' }}>{t('portal_tab.theme_section_hint')}</p>
                    <div className="portal-theme-selector">
                        {PORTAL_THEMES.map(themeId => (
                            <button
                                type="button"
                                key={themeId}
                                className={`portal-theme-card ${portalTheme === themeId ? '-active' : ''}`}
                                style={{ '--thumb-accent': accent }}
                                onClick={() => onSelectTheme(themeId)}
                                disabled={savingTheme}
                            >
                                <div className="portal-theme-thumb">{renderThemeThumb(themeId, accent)}</div>
                                <div className="portal-theme-meta">
                                    <div className="portal-theme-name-row">
                                        <span className="portal-theme-name">{t(`portal_tab.theme_${themeId.toLowerCase()}_name`)}</span>
                                        {portalTheme === themeId && <FaIcon iconName="circle-check" className="portal-theme-check" />}
                                    </div>
                                    <span className="portal-theme-desc">{t(`portal_tab.theme_${themeId.toLowerCase()}_desc`)}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                    {savingTheme && <div style={{ marginTop: '0.75em' }}><Spinner color="positive small" /></div>}
                </div>

                {/* Preview en vivo del portal real */}
                <div className="pm-appearance-preview">
                    <div className="pm-appearance-preview-label">
                        <FaIcon iconName="eye" />
                        {t('portal_modules.live_preview_label')}
                    </div>
                    <div className="pm-appearance-preview-frame-wrap" ref={frameBoxRef}>
                        <iframe
                            key={previewToken}
                            ref={frameRef}
                            className="pm-appearance-preview-frame"
                            style={frameStyle}
                            title={t('portal_modules.live_preview_label')}
                            src={`/client/${clientId}/preview?embed=1&theme=${portalTheme}`}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
